import { type CardMapType } from '@/defs/card'
import { type GallerySortbyDirectionType, type GallerySortbySlugType } from '@/defs/gallery'
import { COLOR_IDS, RARITIES } from '@/defs/mtg'
import { PRICE_SOURCES, type PriceSourceType } from '@/defs/price'
import { type RecType } from '@/defs/rec'
import { compareText, recordFromArray } from '@/utils/commonUtil'

type SortByFnType = (a: RecType, b: RecType) => number
type SortbyConfigType = Record<GallerySortbySlugType, SortByFnType>

export type Props = {
  cardMap: CardMapType
  priceSource: PriceSourceType
  recs: RecType[]
  sortby: GallerySortbySlugType
  sortbyDirection: GallerySortbyDirectionType
}

const helperPrice =
  ({ cardMap, priceSource }: Pick<Props, 'priceSource' | 'cardMap'>): SortByFnType =>
  (a, b) =>
    (cardMap[b.cardId]?.priceSources?.[priceSource]?.price ?? -Infinity) -
    (cardMap[a.cardId]?.priceSources?.[priceSource]?.price ?? -Infinity)

export const getSortedGallery = ({ cardMap, priceSource, recs, sortby, sortbyDirection }: Props) => {
  const isCountModeAvailable = recs.some((r) => r.numDecks)
  const fallback = isCountModeAvailable ? 'count' : 'name'

  if (sortby === 'count') sortby = fallback
  else if (sortby === 'inclusion' && !recs.some((r) => r.inclusion !== undefined)) sortby = fallback
  else if (sortby === 'synergy' && !recs.some((r) => r.synergy !== undefined)) sortby = fallback
  else if (sortby === 'rank' && !recs.some((r) => r.rank !== undefined)) sortby = fallback

  const SORTBY_CONFIG: SortbyConfigType = {
    colorId: (a, b) =>
      (COLOR_IDS.findIndex((r) => r === cardMap[a.cardId]?.colorId) || -1) -
      (COLOR_IDS.findIndex((r) => r === cardMap[b.cardId]?.colorId) || -1),
    count: (a, b) => (b.numDecks || 0) - (a.numDecks || 0),
    default: (a, b) => (a.defaultOrder || 0) - (b.defaultOrder || 0),
    inclusion: (a, b) => (b.inclusion || 0) - (a.inclusion || 0),
    lift: (a, b) => (b.lift || 0) - (a.lift || 0),
    mv: (a, b) => (cardMap[a.cardId]?.cmc || 0) - (cardMap[b.cardId]?.cmc || 0),
    name: (a, b) => compareText(a.name || '', b.name || ''),
    potentialDecks: (a, b) => (b.potentialDecks || 0) - (a.potentialDecks || 0),
    'price-preferred': helperPrice({ cardMap, priceSource }),
    rank: (a, b) => (a.rank || 0) - (b.rank || 0),
    rarity: (a, b) =>
      RARITIES.findIndex((r) => r === cardMap[a.cardId]?.rarity?.toLowerCase()) -
      RARITIES.findIndex((r) => r === cardMap[b.cardId]?.rarity?.toLowerCase()),
    releaseDate: (a, b) =>
      new Date(cardMap[b.cardId]?.releasedAt || '').getTime() - new Date(cardMap[a.cardId]?.releasedAt || '').getTime(),
    salt: (a, b) => (b.saltRec ?? (cardMap[b.cardId]?.salt || 0)) - (a.saltRec ?? (cardMap[a.cardId]?.salt || 0)),
    spoilDate: (a, b) =>
      new Date(cardMap[b.cardId]?.spoiledAt || cardMap[b.cardId]?.releasedAt || '').getTime() -
      new Date(cardMap[a.cardId]?.spoiledAt || cardMap[a.cardId]?.releasedAt || '').getTime(),
    synergy: (a, b) => (b.synergy || 0) - (a.synergy || 0),
    type: (a, b) => compareText(cardMap[a.cardId]?.type || '', cardMap[b.cardId]?.type || ''),
    ...recordFromArray(PRICE_SOURCES, (ps) => [`price-${ps}`, helperPrice({ cardMap, priceSource: ps })]),
  } as const

  const recsSorted = [...recs].sort(SORTBY_CONFIG[sortby])
  if (sortbyDirection === 'asc') recsSorted.reverse()

  return recsSorted
}
